import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArtistCard from './ArtistCard';

interface Artist {
  id: string;
  name: string;
  category: string;
  genre: string;
  bio: string;
  imageUrl: string;
  avatarUrl: string;
  featured: boolean;
  eventTypes: string[];
  phone: string;
  email: string;
  website: string;
  social: {
    instagram: string;
    facebook: string;
    youtube: string;
  };
}

interface ArtistGridProps {
  artists: Artist[];
}

const ITEMS_PER_PAGE = 9;

export default function ArtistGrid({ artists: allArtists }: ArtistGridProps) {
  const { t } = useTranslation('artists');
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get('q')?.toLowerCase() || '';
  const category = searchParams.get('category') || 'all';
  const genre = searchParams.get('genre') || 'all';
  const event = searchParams.get('event') || 'all';
  const sort = searchParams.get('sort') || 'featured';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const filteredArtists = useMemo(() => {
    let result = [...allArtists];

    // Search query
    if (query) {
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(query) ||
          a.genre.toLowerCase().includes(query) ||
          a.bio.toLowerCase().includes(query) ||
          a.category.toLowerCase().includes(query) ||
          a.eventTypes.some((et) => et.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (category !== 'all') {
      result = result.filter(
        (a) => a.category.toLowerCase() === category.toLowerCase()
      );
    }

    // Genre filter
    if (genre !== 'all') {
      result = result.filter((a) => a.genre === genre);
    }

    // Event filter
    if (event !== 'all') {
      result = result.filter((a) => a.eventTypes.includes(event));
    }

    // Sort
    if (sort === 'featured') {
      result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || a.name.localeCompare(b.name));
    } else if (sort === 'name') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'category') {
      result.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    }

    return result;
  }, [allArtists, query, category, genre, event, sort]);

  const totalPages = Math.ceil(filteredArtists.length / ITEMS_PER_PAGE);
  const paginatedArtists = filteredArtists.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      {/* Results Count */}
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {t('resultsCount', { count: filteredArtists.length })}
        </p>
      </div>

      {/* No Results */}
      {filteredArtists.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gray-100 rounded-full mb-4">
            <i className="ri-search-line text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noResults')}</h3>
          <p className="text-sm text-gray-500 mb-4">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {paginatedArtists.map((artist) => (
          <ArtistCard key={artist.id} artist={artist} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-gray-900 text-white'
                  : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      )}
    </div>
  );
}